import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ChevronRight, Sparkles, Heart, MessageSquare, Building2, Globe, Users, CreditCard } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/hooks/useAuth";
import { useUpcomingBookings } from "@/hooks/useBookings";
import { useUserCommunity } from "@/hooks/useUserCommunity";
import { format, parseISO } from "date-fns";

// Landing page components — hero + signup paths load eagerly, rest lazy
import { HeroSection } from "@/components/landing/HeroSection";
import { SignupPathsSection } from "@/components/landing/SignupPathsSection";
import { RevealOnScroll } from "@/components/landing/RevealOnScroll";
import { LazySection } from "@/components/landing/LazySection";
import { FooterSection } from "@/components/landing/FooterSection";
import { HelpWidget } from "@/components/common/HelpWidget";
import { useSEO } from "@/hooks/useSEO";

import { faqs } from "@/components/landing/FAQSection";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function Index() {
  const { user, loading, profile, isSuperAdmin, isBusinessAdmin, isStaff } = useAuth();
  const { data: upcomingBookings } = useUpcomingBookings();
  const { data: userCommunity } = useUserCommunity();

  useSEO({
    title: "Free Scheduling Software for Every Business",
    description: "Free scheduling software for salons, clinics, consultants, and service professionals. Online booking, reminders, payments, staff management and more.",
    path: "/",
    jsonLd: faqJsonLd,
  });

  

  const isCustomer = !isSuperAdmin && !isBusinessAdmin && !isStaff;

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return format(date, "h:mm a");
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Logged in user view
  if (user) {
    return (
      <AppLayout>
        <div className="container px-4 py-6">
          {/* Welcome section */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">
              Hi, {profile?.full_name?.split(" ")[0] || "there"}
            </h1>
          <p className="text-muted-foreground">
              {isSuperAdmin
                ? "Manage your platform"
                : isBusinessAdmin
                ? "Manage your business"
                : isStaff
                ? "Check your appointments"
                : "Book your next appointment"}
          </p>
          </div>

          {/* Quick actions */}
          <div className="grid gap-4">
            {/* Book appointment card - for customers only */}
            {isCustomer && (
              <Link
                to="/book"
                className="group relative overflow-hidden rounded-2xl gradient-primary p-6 shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]"
              >
                <div className="relative z-10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Book Appointment</h3>
                  <p className="mt-1 text-white/80">Find available slots with your service provider</p>
                </div>
                <ChevronRight className="absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 text-white/60 transition-transform group-hover:translate-x-1" />
              </Link>
            )}

            {/* Super Admin quick actions */}
            {isSuperAdmin && (
              <Link
                to="/admin"
                className="group relative overflow-hidden rounded-2xl gradient-primary p-6 shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]"
              >
                <div className="relative z-10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Admin Dashboard</h3>
                  <p className="mt-1 text-white/80">Manage the platform</p>
                </div>
                <ChevronRight className="absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 text-white/60 transition-transform group-hover:translate-x-1" />
              </Link>
            )}

            {/* Business Admin quick actions */}
            {isBusinessAdmin && !isSuperAdmin && (
              <Link
                to="/business"
                className="group relative overflow-hidden rounded-2xl gradient-primary p-6 shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]"
              >
                <div className="relative z-10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Business Dashboard</h3>
                  <p className="mt-1 text-white/80">Manage your business</p>
                </div>
                <ChevronRight className="absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 text-white/60 transition-transform group-hover:translate-x-1" />
              </Link>
            )}

            {/* Staff quick actions */}
            {isStaff && !isSuperAdmin && !isBusinessAdmin && (
              <Link
                to="/staff"
                className="group relative overflow-hidden rounded-2xl gradient-primary p-6 shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]"
              >
                <div className="relative z-10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">My Dashboard</h3>
                  <p className="mt-1 text-white/80">View your calendar</p>
                </div>
                <ChevronRight className="absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 text-white/60 transition-transform group-hover:translate-x-1" />
              </Link>
            )}

            {/* Secondary cards - role-specific */}
            <div className="grid grid-cols-2 gap-4">
              {/* Super Admin specific cards */}
              {isSuperAdmin ? (
                <>
                  <Link
                    to="/admin/tickets"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <MessageSquare className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">Support Tickets</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">View open tickets</p>
                  </Link>

                  <Link
                    to="/admin/communities"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Building2 className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">Communities</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">Active businesses</p>
                  </Link>
                </>
              ) : isBusinessAdmin ? (
                <>
                  <Link
                    to="/business/hr"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Users className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">Human Resources</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">Manage HR</p>
                  </Link>

                  <Link
                    to="/business/cards"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <CreditCard className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">Greeting Cards</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">Send cards</p>
                  </Link>

                  <Link
                    to="/bookings"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Clock className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">My Bookings</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">View all bookings</p>
                  </Link>

                  {userCommunity?.slug ? (
                    <Link
                      to={`/b/${userCommunity.slug}`}
                      className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <Globe className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                      <h4 className="font-medium text-foreground group-hover:text-accent-foreground">View Public Page</h4>
                      <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">Your business profile</p>
                    </Link>
                  ) : (
                    <div
                      aria-disabled
                      className="rounded-xl border border-border bg-card p-4 opacity-60"
                    >
                      <Globe className="mb-3 h-6 w-6 text-primary" />
                      <h4 className="font-medium text-foreground">View Public Page</h4>
                      <p className="text-sm text-muted-foreground">Loading business…</p>
                    </div>
                  )}
                </>
              ) : isStaff ? (
                <>
                  <Link
                    to="/bookings"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Clock className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">My Bookings</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">View all bookings</p>
                  </Link>

                  {userCommunity?.slug ? (
                    <Link
                      to={`/b/${userCommunity.slug}`}
                      className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <Globe className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                      <h4 className="font-medium text-foreground group-hover:text-accent-foreground">View Public Page</h4>
                      <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">Your business profile</p>
                    </Link>
                  ) : (
                    <div aria-disabled className="rounded-xl border border-border bg-card p-4 opacity-60">
                      <Globe className="mb-3 h-6 w-6 text-primary" />
                      <h4 className="font-medium text-foreground">View Public Page</h4>
                      <p className="text-sm text-muted-foreground">Loading business…</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to="/bookings"
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Clock className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">My Bookings</h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">View all bookings</p>
                  </Link>

                  <Link
                    to={userCommunity?.slug ? `/b/${userCommunity.slug}` : "/explore"}
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {userCommunity?.logo_url ? (
                      <img
                        src={userCommunity.logo_url}
                        alt={userCommunity.name}
                        className="mb-3 h-6 w-6 rounded object-cover"
                      />
                    ) : (
                      <Building2 className="mb-3 h-6 w-6 text-primary group-hover:text-accent-foreground" />
                    )}
                    <h4 className="font-medium text-foreground group-hover:text-accent-foreground">
                      {userCommunity?.name || "Find a Business"}
                    </h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">
                      {userCommunity ? "My Provider" : "Explore businesses"}
                    </p>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Upcoming appointments - hide for super admin */}
          {!isSuperAdmin && (
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Upcoming</h2>
                <Link to="/bookings" className="text-sm text-primary hover:underline">
                  View all
                </Link>
              </div>
              {upcomingBookings && upcomingBookings.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {upcomingBookings.slice(0, 2).map((booking) => (
                    <Link
                      key={booking.id}
                      to="/bookings"
                      className="group flex items-center gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-primary/10">
                        <span className="text-xs font-medium text-primary group-hover:text-accent-foreground">
                          {format(parseISO(booking.booking_date), "MMM")}
                        </span>
                        <span className="text-lg font-bold text-primary group-hover:text-accent-foreground">
                          {format(parseISO(booking.booking_date), "d")}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground group-hover:text-accent-foreground truncate">
                          {booking.service?.name || "Appointment"}
                        </h4>
                    <p className="text-sm text-muted-foreground group-hover:text-accent-foreground/80">
                          {formatTime(booking.start_time)} • {booking.barber?.full_name}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border p-8 text-center">
                  <Calendar className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No upcoming appointments</p>
                  {isCustomer && (
                    <Button asChild className="mt-4 text-primary-foreground" variant="default">
                      <Link to="/book">Book now</Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </AppLayout>
    );
  }



  // Landing page for unauthenticated users
  return (
    <div className="min-h-screen bg-background">
      <HeroSection />
      <main>
        <SignupPathsSection />
        <RevealOnScroll>
          <LazySection factory={() => import("@/components/landing/FeaturesSection").then(m => ({ default: m.FeaturesSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/IntegrationsCarousel").then(m => ({ default: m.IntegrationsCarousel }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/GreetingCardsSection").then(m => ({ default: m.GreetingCardsSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/AIHRSection").then(m => ({ default: m.AIHRSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/StepsSection").then(m => ({ default: m.StepsSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/TestimonialsSection").then(m => ({ default: m.TestimonialsSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/FAQSection").then(m => ({ default: m.FAQSection }))} />
        </RevealOnScroll>
        <RevealOnScroll delay={30}>
          <LazySection factory={() => import("@/components/landing/CTASection").then(m => ({ default: m.CTASection }))} />
        </RevealOnScroll>
      </main>
      <FooterSection />
      <HelpWidget />
    </div>
  );
}
